import React from 'react';
import { UseFormRegister } from 'react-hook-form';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

interface FormSelectProps {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  required?: boolean;
  register: UseFormRegister<any>;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
}

export default function FormSelect({
  label,
  name,
  options,
  required = false,
  register,
  error,
  placeholder,
  disabled,
}: FormSelectProps) {
  const { theme } = useTheme();

  return (
    <div className="relative">
      <label className={`block text-sm font-medium mb-1 ${
        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
      }`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <select
          {...register(name, { required: required && `${label} is required` })}
          defaultValue=""
          disabled={disabled}
          className={`
            block w-full rounded-md px-3 py-2 text-base 
            border border-border
            appearance-none
            shadow-sm
            focus:outline-none focus:ring-2 focus:ring-offset-0
            transition-colors duration-200
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
            ${error
              ? theme === 'dark'
                ? 'border-red-500 text-red-200 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : theme === 'dark'
              ? 'focus:border-blue-500 focus:ring-blue-500 hover:border-gray-500'
              : 'focus:border-blue-500 focus:ring-blue-500'
            }
            ${disabled && theme === 'dark'
              ? 'bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed'
              : disabled
              ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed'
              : ''
            }
          `}
          aria-invalid={error ? 'true' : 'false'}
        >
          <option value="" disabled className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
            {placeholder || `Select ${label.toLowerCase()}`}
          </option>
          {options.map(option => (
            <option
              key={option.value}
              value={option.value}
              className={`py-2 ${
                theme === 'dark' ? 'text-gray-100 bg-gray-700' : 'text-gray-900 bg-white'
              }`}
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <ChevronDown className="h-5 w-5" aria-hidden="true" />
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}